import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, Users, Eye, Clock, Activity, MapPin, Euro, CreditCard, Monitor, Smartphone, Tablet, ExternalLink, FileText, RefreshCw } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface SubscriptionStats {
  totalRevenue: number;
  monthlyCount: number;
  monthlyRevenue: number;
  annualCount: number;
  annualRevenue: number;
}

interface StatsData {
  date: string;
  inscriptions: number;
  visites: number;
}

interface LovableAnalytics {
  timeSeries: {
    visitors: { total: number };
    pageviews: { total: number };
    pageviewsPerVisit: { total: number };
    sessionDuration: { total: number };
    bounceRate: { total: number };
    daily?: Array<{ date: string; value: number }>;
  };
  lists: {
    page: { data: Array<{ label: string; value: number }> };
    source: { data: Array<{ label: string; value: number }> };
    device: { data: Array<{ label: string; value: number }> };
    country: { data: Array<{ label: string; value: number; name?: string }> };
  };
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${minutes}m ${secs}s`;
};

const COUNTRY_NAMES: Record<string, string> = {
  'FR': 'France',
  'US': 'États-Unis',
  'NL': 'Pays-Bas',
  'DE': 'Allemagne',
  'GB': 'Royaume-Uni',
  'BE': 'Belgique',
  'CH': 'Suisse',
  'CA': 'Canada',
  'ES': 'Espagne',
  'IT': 'Italie',
  'Unknown': 'Inconnu'
};

export const AdminStats = () => {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | 'all'>('90d');
  const [statsData, setStatsData] = useState<StatsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalInscriptions, setTotalInscriptions] = useState(0);
  const [totalBateaux, setTotalBateaux] = useState(0);
  const [subscriptionStats, setSubscriptionStats] = useState<SubscriptionStats>({
    totalRevenue: 0,
    monthlyCount: 0,
    monthlyRevenue: 0,
    annualCount: 0,
    annualRevenue: 0
  });
  const [lovableStats, setLovableStats] = useState<LovableAnalytics | null>(null);

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchLovableAnalytics = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('lovable-analytics', {
        body: { period }
      });
      
      if (data?.success && data?.data) {
        setLovableStats(data.data);
      }
    } catch (error) {
      console.error('Erreur analytics Lovable:', error);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      let startDate: Date;
      let days: number;
      
      switch (period) {
        case '7d':
          startDate = subDays(endDate, 7);
          days = 7;
          break;
        case '90d':
          startDate = subDays(endDate, 90);
          days = 90;
          break;
        case 'all':
          startDate = new Date('2025-06-01');
          days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          break;
        default:
          startDate = subDays(endDate, 30);
          days = 30;
      }

      // Fetch Lovable analytics
      await fetchLovableAnalytics();

      // Récupérer le nombre total d'utilisateurs
      const { count: usersCount } = await supabase
        .from('profils_utilisateur')
        .select('*', { count: 'exact', head: true });
      
      setTotalUsers(usersCount || 0);

      // Récupérer le nombre total de bateaux
      const { count: bateauxCount } = await supabase
        .from('bateaux')
        .select('*', { count: 'exact', head: true });
      
      setTotalBateaux(bateauxCount || 0);

      // Récupérer les statistiques des abonnements actifs
      const { data: abonnementsData } = await supabase
        .from('abonnements')
        .select('plan')
        .eq('statut', 'actif')
        .gte('date_fin', new Date().toISOString());

      // Récupérer les prix configurés
      const { data: pricingData } = await supabase
        .from('configuration_prix')
        .select('plan, prix')
        .eq('actif', true);

      const pricingMap: Record<string, number> = {};
      pricingData?.forEach((p: any) => {
        pricingMap[p.plan] = p.prix;
      });

      let monthlyCount = 0;
      let annualCount = 0;
      
      abonnementsData?.forEach((abo: any) => {
        if (abo.plan === 'mensuel') monthlyCount++;
        else if (abo.plan === 'annuel') annualCount++;
      });

      const monthlyPrice = pricingMap['mensuel'] || 9.99;
      const annualPrice = pricingMap['annuel'] || 99.99;
      
      const monthlyRevenue = monthlyCount * monthlyPrice;
      const annualRevenue = annualCount * annualPrice;

      setSubscriptionStats({
        totalRevenue: monthlyRevenue + annualRevenue,
        monthlyCount,
        monthlyRevenue,
        annualCount,
        annualRevenue
      });

      // Récupérer les inscriptions par jour
      const { data: profilesData } = await supabase
        .from('profils_utilisateur')
        .select('created_at')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: true });

      // Grouper par jour
      const statsMap = new Map<string, { inscriptions: number; visites: number }>();
      
      for (let i = days - 1; i >= 0; i--) {
        const date = subDays(endDate, i);
        const dateKey = format(date, 'yyyy-MM-dd');
        statsMap.set(dateKey, { inscriptions: 0, visites: 0 });
      }

      // Compter les inscriptions par jour
      profilesData?.forEach((profile) => {
        const dateKey = format(new Date(profile.created_at), 'yyyy-MM-dd');
        if (statsMap.has(dateKey)) {
          const current = statsMap.get(dateKey)!;
          statsMap.set(dateKey, { ...current, inscriptions: current.inscriptions + 1 });
        }
      });

      // Convertir en array pour le graphique
      const chartData: StatsData[] = Array.from(statsMap.entries()).map(([date, data]) => ({
        date: format(new Date(date), 'dd MMM', { locale: fr }),
        inscriptions: data.inscriptions,
        visites: data.visites,
      }));

      setStatsData(chartData);
      
      const totalInsc = chartData.reduce((sum, d) => sum + d.inscriptions, 0);
      setTotalInscriptions(totalInsc);

    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Chargement des statistiques...
        </div>
      </div>
    );
  }

  const visitors = lovableStats?.timeSeries?.visitors?.total || 0;
  const pageviews = lovableStats?.timeSeries?.pageviews?.total || 0;
  const viewsPerVisit = lovableStats?.timeSeries?.pageviewsPerVisit?.total || 0;
  const sessionDuration = lovableStats?.timeSeries?.sessionDuration?.total || 0;
  const bounceRate = lovableStats?.timeSeries?.bounceRate?.total || 0;

  const dailyVisitsData = lovableStats?.timeSeries?.daily || [];
  const pageData = lovableStats?.lists?.page?.data || [];
  const sourceData = lovableStats?.lists?.source?.data || [];
  const deviceData = lovableStats?.lists?.device?.data || [];
  const countryData = lovableStats?.lists?.country?.data || [];

  const totalDevices = deviceData.reduce((sum, d) => sum + d.value, 0) || 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-nautic">Statistiques du site</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchStats} className="border-nautic-gold/50 hover:bg-nautic-gold/10">
            <RefreshCw className="h-4 w-4 mr-1 text-nautic-gold" />
            Actualiser
          </Button>
          <Select value={period} onValueChange={(v) => setPeriod(v as '7d' | '30d' | '90d' | 'all')}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Période" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Depuis le début</SelectItem>
              <SelectItem value="90d">90 derniers jours</SelectItem>
              <SelectItem value="30d">30 derniers jours</SelectItem>
              <SelectItem value="7d">7 derniers jours</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* === STATISTIQUES INTERNES EN PREMIER === */}
      
      {/* Cards de statistiques utilisateurs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-nautic-gold">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Utilisateurs</CardTitle>
            <Users className="h-4 w-4 text-nautic-gold" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-nautic-gold">{totalUsers}</div>
            <p className="text-xs text-muted-foreground">Comptes créés</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bateaux enregistrés</CardTitle>
            <Activity className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{totalBateaux}</div>
            <p className="text-xs text-muted-foreground">Dans la base</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Nouvelles inscriptions</CardTitle>
            <TrendingUp className="h-4 w-4 text-nautic-gold" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalInscriptions}</div>
            <p className="text-xs text-muted-foreground">Sur la période</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taux de conversion</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {visitors > 0 ? ((totalInscriptions / visitors) * 100).toFixed(1) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">Visiteurs → Inscrits</p>
          </CardContent>
        </Card>
      </div>

      {/* Cards Revenus Abonnements */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-nautic-gold bg-gradient-to-br from-nautic-gold/10 to-transparent">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenus Totaux</CardTitle>
            <Euro className="h-4 w-4 text-nautic-gold" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-nautic-gold">{subscriptionStats.totalRevenue.toFixed(2)} €</div>
            <p className="text-xs text-muted-foreground">Abonnements actifs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Abonnements Mensuels</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{subscriptionStats.monthlyCount}</div>
            <p className="text-xs text-muted-foreground">{subscriptionStats.monthlyRevenue.toFixed(2)} € / mois</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Abonnements Annuels</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{subscriptionStats.annualCount}</div>
            <p className="text-xs text-muted-foreground">{subscriptionStats.annualRevenue.toFixed(2)} € / an</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MRR estimé</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {(subscriptionStats.monthlyRevenue + (subscriptionStats.annualRevenue / 12)).toFixed(2)} €
            </div>
            <p className="text-xs text-muted-foreground">Revenu mensuel récurrent</p>
          </CardContent>
        </Card>
      </div>

      {/* Graphique des inscriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="w-1 h-5 bg-nautic-gold rounded-full"></span>
            Évolution des inscriptions
          </CardTitle>
          <CardDescription>Nombre de nouvelles inscriptions par jour</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statsData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  width={30}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                <Bar 
                  dataKey="inscriptions" 
                  name="Inscriptions"
                  fill="#E8A306"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Graphique des visites quotidiennes */}
      {dailyVisitsData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="w-1 h-5 bg-primary rounded-full"></span>
              Visites quotidiennes
            </CardTitle>
            <CardDescription>Nombre de pages vues par jour (données réelles)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyVisitsData.map(d => ({ date: d.date.slice(5), visites: d.value }))}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    width={30}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Line 
                    type="monotone"
                    dataKey="visites" 
                    name="Pages vues"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* === SÉPARATEUR ANALYTICS === */}
      <div className="border-t border-nautic-gold/30 pt-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-1 h-5 bg-nautic-gold rounded-full"></span>
          <h2 className="text-lg font-semibold text-foreground">Analytics détaillées</h2>
          <Badge variant="outline" className="text-xs border-nautic-gold/50 text-nautic-gold">Temps réel</Badge>
        </div>
      </div>

      {/* Cards de statistiques Lovable-style - 5 métriques principales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-nautic text-white">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium opacity-80">Visitors</p>
            <div className="text-2xl font-bold">{visitors.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-muted-foreground">Pageviews</p>
            <div className="text-2xl font-bold">{pageviews.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-muted-foreground">Views Per Visit</p>
            <div className="text-2xl font-bold">{viewsPerVisit.toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-muted-foreground">Visit Duration</p>
            <div className="text-2xl font-bold">{formatDuration(sessionDuration)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-muted-foreground">Bounce Rate</p>
            <div className="text-2xl font-bold">{Math.round(bounceRate)}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Source et Page */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Source */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-nautic-gold" />
                Source
              </CardTitle>
              <span className="text-xs text-muted-foreground">Visitors</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {sourceData.length > 0 ? (
              sourceData.map((item) => {
                const maxCount = sourceData[0]?.value || 1;
                const percentage = (item.value / maxCount) * 100;
                return (
                  <div key={item.label} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        {item.label}
                      </span>
                      <span className="font-medium">{item.value}</span>
                    </div>
                    <Progress value={percentage} className="h-2 [&>div]:bg-nautic-gold" />
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>

        {/* Page */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4 text-nautic-gold" />
                Page
              </CardTitle>
              <span className="text-xs text-muted-foreground">Visitors</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {pageData.length > 0 ? (
              pageData.map((item) => {
                const maxCount = pageData[0]?.value || 1;
                const percentage = (item.value / maxCount) * 100;
                return (
                  <div key={item.label} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        {item.label}
                      </span>
                      <span className="font-medium">{item.value}</span>
                    </div>
                    <Progress value={percentage} className="h-2 [&>div]:bg-primary" />
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Country et Device */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Country */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4 text-nautic-gold" />
                Country
              </CardTitle>
              <span className="text-xs text-muted-foreground">Visitors</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {countryData.length > 0 ? (
              countryData.map((item) => {
                const maxCount = countryData[0]?.value || 1;
                const percentage = (item.value / maxCount) * 100;
                const countryName = COUNTRY_NAMES[item.label] || item.name || item.label;
                return (
                  <div key={item.label} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        {item.label !== 'Unknown' && (
                          <span className={`fi fi-${item.label.toLowerCase()}`} />
                        )}
                        {countryName}
                      </span>
                      <span className="font-medium">{item.value}</span>
                    </div>
                    <Progress value={percentage} className="h-2 [&>div]:bg-nautic" />
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>

        {/* Device */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Monitor className="h-4 w-4 text-nautic-gold" />
                Device
              </CardTitle>
              <span className="text-xs text-muted-foreground">Visitors</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {deviceData.length > 0 ? (
              deviceData.map((item) => {
                const percentage = (item.value / totalDevices) * 100;
                const DeviceIcon = item.label.toLowerCase() === 'mobile' 
                  ? Smartphone 
                  : item.label.toLowerCase() === 'tablet' 
                    ? Tablet 
                    : Monitor;
                return (
                  <div key={item.label} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <DeviceIcon className="h-3 w-3 text-muted-foreground" />
                        {item.label.charAt(0).toUpperCase() + item.label.slice(1)}
                      </span>
                      <span className="font-medium">{percentage.toFixed(1)}%</span>
                    </div>
                    <Progress value={percentage} className="h-2 [&>div]:bg-nautic-gold" />
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Note informative */}
      <Card className="bg-nautic-gold/5 border-nautic-gold/20">
        <CardContent className="pt-4">
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4 text-nautic-gold" />
            Les statistiques de trafic proviennent de la table des visites en temps réel. Les données internes (utilisateurs, bateaux, revenus) sont également en temps réel.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
